import React, { useContext, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextStyle } from '@superops/ui-design-system';
import { getTranslatedText } from '@nile/components';
import SimilarTicketCard from '../SilimarTicketCard';
import AISimilarTicketsHeader from './AISimilarTicketsHeader';
import MonicaLoader from '../MonicaLoader';
import AIContainerContext from '../../context/AIContainerContext';
import { useSimilarTicketsStore } from '@nile/components/src/Components/AI/store/similarTicketsStore.tsx';

const AISimilarTickets = (props) => {
  const { content, data, hideHeader, mixpanelEventOnOpen } = props;
  const intl = useIntl();

  const SimilarTicketEmptyStateText = [
    getTranslatedText(intl, 'msp.ai.similar.tickets.empty.message.1'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.empty.message.2'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.empty.message.3'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.empty.message.4')
  ];

  const unresolvedTicketLoadingText = [
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.loading.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.loading.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.loading.message.3'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.loading.message.4'
    )
  ];
  const unresolvedHeaderText = [
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.tickets.success.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.tickets.success.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.tickets.success.message.3'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.tickets.success.message.4'
    )
  ];
  const emptyUnresolvedHeaderText = [
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.empty.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.empty.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.unresolved.empty.message.3'
    ),
    getTranslatedText(intl, 'msp.ai.similar.tickets.unresolved.empty.message.4')
  ];

  const resolvedTicketLoadingText = [
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.loading.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.loading.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.loading.message.3'
    ),
    getTranslatedText(intl, 'msp.ai.similar.tickets.resolved.loading.message.4')
  ];
  const resolvedHeaderText = [
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.tickets.success.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.tickets.success.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.tickets.success.message.3'
    ),
    getTranslatedText(
      intl,
      'msp.ai.similar.tickets.resolved.tickets.success.message.4'
    )
  ];
  const emptyResolvedHeaderText = [
    getTranslatedText(intl, 'msp.ai.similar.tickets.resolved.empty.message.1'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.resolved.empty.message.2'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.resolved.empty.message.3'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.resolved.empty.message.4')
  ];

  const getMetaData = ({ headerState, tickets }) => {
    const loadingMap = {
      resolved: resolvedTicketLoadingText,
      unresolved: unresolvedTicketLoadingText
    };

    const headerTextMap = {
      resolved: isEmpty(tickets) ? emptyResolvedHeaderText : resolvedHeaderText,
      unresolved: isEmpty(tickets)
        ? emptyUnresolvedHeaderText
        : unresolvedHeaderText
    };
    return {
      loadingText: loadingMap?.[headerState] || null,
      headerText: headerTextMap?.[headerState] || null
    };
  };

  const currentTicketId = data?.variables?.ticketId;
  const allSimilarTicketsData = content || null;

  const similarTicketsData = useSimilarTicketsStore(
    (state) => state.similarTicketsData
  );
  const setSimilarTicketsData = useSimilarTicketsStore(
    (state) => state.setSimilarTicketsData
  );

  const selectedHeader = useSimilarTicketsStore(
    (state) => state.selectedHeader
  );
  const setSelectedHeader = useSimilarTicketsStore(
    (state) => state.setSelectedHeader
  );

  const aiContainerContext = useContext(AIContainerContext);

  useEffect(() => {
    if (!similarTicketsData) {
      setSimilarTicketsData(content);
      if (isEmpty(content)) {
        aiContainerContext?.setCustomHeaderText(SimilarTicketEmptyStateText);
      }
    }
  }, [content]);

  const [isMonicaLoading, setIsMonicaLoading] = useState(false);
  const [monicaLoadingText, setMonicaLoadingText] = useState(null);

  const onHeaderChange = (updatedValue) => {
    setSelectedHeader(updatedValue);

    if (updatedValue === 'all') {
      setSimilarTicketsData(allSimilarTicketsData);
      setMonicaLoadingText(null);
      aiContainerContext.setCustomHeaderText(null);
    } else {
      const condition = updatedValue === 'resolved' ? true : false;
      const filteredData = allSimilarTicketsData.filter(
        (ticket) => ticket.resolved === condition
      );
      setSimilarTicketsData(filteredData);
      const metaData = getMetaData({
        headerState: updatedValue,
        tickets: filteredData
      });
      if (metaData?.loadingText) {
        setMonicaLoadingText(metaData?.loadingText);
      }

      if (metaData?.headerText && aiContainerContext?.setCustomHeaderText) {
        aiContainerContext.setCustomHeaderText(metaData?.headerText);
      }
    }
  };

  if (isEmpty(allSimilarTicketsData) && !isMonicaLoading) {
    return <></>;
  }

  return (
    <div
      id="ai-similar-ticket-parent"
      className={` ai-content-container-max-height scroll ${
        isMonicaLoading
          ? 'no-scroll min-height-170'
          : 'scroll position-relative'
      }`}
    >
      <MonicaLoader
        loading={isMonicaLoading}
        classNames="full-height"
        loadingText={monicaLoadingText}
      />

      {!hideHeader && (
        <>
          <AISimilarTicketsHeader
            selectedHeader={selectedHeader}
            onHeaderChange={onHeaderChange}
            setIsMonicaLoading={setIsMonicaLoading}
            data={similarTicketsData || []}
            currentTicketId={currentTicketId}
            isMonicaLoading={isMonicaLoading}
            allSimilarTicketsData={allSimilarTicketsData}
          />

          {selectedHeader !== 'all' && !isEmpty(similarTicketsData) && (
            <div className="mb10 fsize12">
              <TextStyle uppercase>
                <FormattedMessage id="msp.ai.feature.similar.tickets" /> (
                {similarTicketsData?.length})
              </TextStyle>
            </div>
          )}
        </>
      )}

      <div className="ai-similar-tickets-container">
        {!isEmpty(similarTicketsData) ? (
          <div className="flex fcol g10">
            {similarTicketsData.map((similarTicket) => {
              const {
                ticketId,
                displayId,
                subject,
                siteName,
                resolved,
                clientName,
                summary
              } = similarTicket;

              return (
                <React.Fragment key={ticketId}>
                  <SimilarTicketCard
                    ticketId={ticketId}
                    displayId={displayId}
                    subject={subject}
                    siteName={siteName}
                    resolved={resolved}
                    clientName={clientName}
                    summary={summary}
                    mixpanelEventOnOpen={
                      mixpanelEventOnOpen || {
                        event: 'Clicked on ticket link from similar tickets'
                      }
                    }
                  />
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default AISimilarTickets;
